import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {FormBuilder, Validators} from "@angular/forms";
import { VentanaFlotante } from '../models/VentanaFlotante';
import { HojasCallCenterComponent } from '../hojas-call-center.component';
import { TipoBusqueda, Colaborador, ColaboradorService } from '../services/bd/CallCenter/colaborador.service';
import { BdService } from '../services/bd.service';
import { CallCenter } from '../models/InsCallCenter';
import { lastValueFrom } from 'rxjs';
import { getErrorMessage } from '../helpers/error';
import { ListadoColaboradoresComponent } from './listado-colaboradores.component';
import type { Colaborador as ColaboradorModel } from '../models/Colaborador';
import { isEmpty } from '../helpers/string';
import { TipoLlamadaComponent } from './tipo-llamada.component';
import {Validadores} from "../validadores";


@Component({
  selector: 'ral-axa',
  templateUrl: './axa.component.html',
  styleUrls: ['./axa.component.scss']
})
export class AxaComponent extends VentanaFlotante {
  protected readonly TipoBusqueda: typeof TipoBusqueda = TipoBusqueda;
  protected busquedaForm = this.formBuilder.group({
    tipo: [TipoBusqueda.Poblacion, [Validators.required]],
    valor: ["", [Validators.required]],
  });
  protected buscando = false;
  private formularioHojas: HojasCallCenterComponent;
  protected axaForm = this.formBuilder.group({
    numeroColaborador: ["", [Validators.required]],
    nombreColaborador: ["", [Validators.required]],
    telefonoColaborador: ["", [Validators.required]],
    direccionColaborador: ["", [Validators.required]],
    nombreCliente: ["", [Validators.required]],
    telefonoCliente: ["", [Validators.required]],
    nif: [""],
    poliza: [""],
    matricula: ["", [Validators.required, Validadores.matricula()]],
    observaciones: [""],
    cristal: ["", [Validators.required]],
  });
  protected guardando = false;
  protected cristales = [
    "PARABRISAS",
    "LUNETA",
    "DESC. DEL. DER.",
    "DESC. DEL. IZQ.",
    "FIJO",
    "CUSTODIA"
  ];

  constructor(
    public override dialogRef: MatDialogRef<Component>,
    @Inject(MAT_DIALOG_DATA) public override data: {formularioHojas: HojasCallCenterComponent},
    private dialog: MatDialog,
    private colaboradores: ColaboradorService,
    private bd: BdService,
    private formBuilder: FormBuilder,
  )
  {
    super(dialogRef, data);
    this.formularioHojas = data.formularioHojas;
  }

  async buscar(): Promise<boolean> {
    const busqueda = this.busquedaForm.value;
    try {
      this.buscando = true;
      const colaboradores$ = this.colaboradores.buscar(this.formularioHojas.token, Colaborador.AXA, busqueda.tipo!, busqueda.valor!);
      const colaboradores = await lastValueFrom(colaboradores$);
      if(colaboradores.length > 0){
        const listado = this.dialog.open(ListadoColaboradoresComponent, {
          data: { colaboradores },
        });
        const result:ColaboradorModel|boolean = await lastValueFrom(listado.afterClosed());
        if (result !== true && result !== false) {
          const numero = result.taller === 0 ? Colaborador.AXA : result.taller;
          this.axaForm.patchValue({
            numeroColaborador: String(numero),
            nombreColaborador: result.nombre.trim(),
            telefonoColaborador: result.telefono.trim(),
            direccionColaborador: result.direccion.trim(),
          });
          this.resetBusquedaForm();
        }
      }else{
        this.formularioHojas.mostrarArgumentario("Atención", ["No se ha encontrado nigun dato con la información introducida"]);
        return false;
      }

    } catch (e) {
      this.formularioHojas.mostrarArgumentario("Error en consulta", ["El servidor ha devuelto el siguiente error:", getErrorMessage(e)], false, true, true);
      return false;
    } finally {
      this.buscando = false;
    }
    return true;
  }

  resetBusquedaForm(): void {
    this.busquedaForm.patchValue({valor: ''});
  }

  async guardar(): Promise<void> {
    if(this.axaForm.valid){
      const {entrada, origen, tipo} = this.formularioHojas.llamada;
      const nomcli = "AXA AURORA IBERICA S.A.";
      const cif = "A07002967";
      const date = new Date();
      const form = this.axaForm.value;
      const callCenter:CallCenter = {
        empresa: 0,
        hoja: 0,
        hora_hoja: formatDate(date, "HH:mm:ss", "en"),
        fecha_hoja: formatDate(date, "YYYY-MM-dd", "en"),
        albaran: 0,
        fecha_albaran: "0001-01-01",
        cia: nomcli,
        matricula: (form.matricula!).trim(),
        servicio: "",
        peticion: "",
        piloto: entrada.trim(),
        origen: origen.trim(),
        tipo: await this.getTipoLlamada(tipo),
        agenda: "",
        articulo_callcenter: "",
        articulo_taller: "",
        usuario: this.formularioHojas.usuario,
        sms: "",
        estadosms: "",
        nom_persona: (form.nombreCliente!).trim(),
        telf_persona: (form.telefonoCliente!).trim(),
        descripcion_art: (form.cristal!).trim(),
        numero_colabora: parseInt(form.numeroColaborador!, 10),
        nombre_colabora: (form.nombreColaborador!).trim(),
        obser: (form.observaciones!).trim(),
        nif: cif,
        idtrans: 0,
        campa: "",
        email_recogido: "",
      };

      try {
        this.guardando = true;
        await lastValueFrom(this.bd.insertCallCenter(this.formularioHojas.token, [callCenter]));
        this.dialogRef.close();
      } catch (e) {
        this.formularioHojas.mostrarArgumentario("Error al guardar", ["El servidor ha devuelto el siguiente error:", getErrorMessage(e)], false, true, true);
      } finally {
        this.guardando = false;
      }
    }else{
      this.formularioHojas.mostrarArgumentario("Atención", ["No se puede guardar hasta que se rellenen los campos obligatorios:",
      "- Num. colabora.",
      "- Nom. colabora." ,
      "- Direc. colabora." ,
      "- Tel. colabora." ,
      "- Nombre cliente" ,
      "- Teléfono" ,
      "- Matricula" ,
      "- Cristal"], false, true, true);
    }
  }

  async getTipoLlamada(tipo:string): Promise<string> {
    if (!isEmpty(tipo)) {
      return tipo;
    }
    const selector = this.dialog.open(TipoLlamadaComponent, {
      data: { tiposLlamadas: this.formularioHojas.tipoLlamadaList },
    });
    const result:string = await lastValueFrom(selector.afterClosed());
    this.formularioHojas.llamada.tipo = result;
    return result;
  }
}
